// Privacy.jsx
import React from 'react';
import styles from './privacy.module.css';

// Material UI Icons
import PrivacyTipIcon from '@mui/icons-material/PrivacyTip';
import SecurityIcon from '@mui/icons-material/Security';
import DataUsageIcon from '@mui/icons-material/DataUsage';
import EmailIcon from '@mui/icons-material/Email';
import CookieIcon from '@mui/icons-material/Cookie';
import GavelIcon from '@mui/icons-material/Gavel';
import UpdateIcon from '@mui/icons-material/Update';
import ContactSupportIcon from '@mui/icons-material/ContactSupport';
import PersonIcon from '@mui/icons-material/Person';
import AccountCircleIcon from '@mui/icons-material/AccountCircle';
import ComputerIcon from '@mui/icons-material/Computer';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import StorageIcon from '@mui/icons-material/Storage';
import AssessmentIcon from '@mui/icons-material/Assessment';
import PeopleIcon from '@mui/icons-material/People';
import VpnKeyIcon from '@mui/icons-material/VpnKey';
import PhoneIcon from '@mui/icons-material/Phone';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import LockIcon from '@mui/icons-material/Lock';
import RouterIcon from '@mui/icons-material/Router';

const Privacy = () => {
  return (
    <div className={styles.privacyPage}>
      {/* Hero Section */}
      <section className={styles.hero}>
        <div className={styles.heroOverlay}></div>
        <div className={styles.heroContent}>
          <PrivacyTipIcon className={styles.heroIcon} />
          <h1>Privacy Policy</h1>
          <div className={styles.heroLine}></div>
          <p>Your privacy matters to us. Learn how we protect your information.</p>
          <div className={styles.lastUpdated}>
            <UpdateIcon />
            <span>Last Updated: January 15, 2024</span>
          </div>
        </div>
      </section>

      {/* Content Container */}
      <div className={styles.container}>
        {/* Quick Navigation */}
        <div className={styles.quickNav}>
          <h3>Jump to Section</h3>
          <div className={styles.navLinks}>
            <a href="#information">Information We Collect</a>
            <a href="#usage">How We Use Your Information</a>
            <a href="#sharing">Information Sharing</a>
            <a href="#cookies">Cookies & Tracking</a>
            <a href="#security">Data Security</a>
            <a href="#rights">Your Rights</a>
            <a href="#children">Children's Privacy</a>
            <a href="#contact">Contact Us</a>
          </div>
        </div>

        {/* Main Content */}
        <div className={styles.content}>
          {/* Introduction */}
          <section id="introduction" className={styles.section}>
            <h2>Introduction</h2>
            <p>
              At Equity Residences ("we," "our," or "us"), we are committed to protecting your privacy 
              and ensuring the security of your personal information. This Privacy Policy explains how 
              we collect, use, disclose, and safeguard your information when you use our website, 
              apply for accommodation, or interact with our services.
            </p>
            <p>
              By using our website and services, you consent to the data practices described in this policy. 
              If you do not agree with any terms, please do not use our services.
            </p>
          </section>

          {/* Information We Collect */}
          <section id="information" className={styles.section}>
            <div className={styles.sectionHeader}>
              <DataUsageIcon />
              <h2>Information We Collect</h2>
            </div>
            <div className={styles.cardGrid}>
              <div className={styles.infoCard}>
                <div className={styles.cardIcon}>
                  <PersonIcon />
                </div>
                <h3>Personal Information</h3>
                <ul>
                  <li>Full name and date of birth</li>
                  <li>Email address and phone number</li>
                  <li>Physical address</li>
                  <li>Student ID and institution information</li>
                  <li>Government ID or passport details</li>
                  <li>Emergency contact information</li>
                </ul>
              </div>
              <div className={styles.infoCard}>
                <div className={styles.cardIcon}>
                  <AccountCircleIcon />
                </div>
                <h3>Account Information</h3>
                <ul>
                  <li>Login credentials</li>
                  <li>Application history</li>
                  <li>Payment information</li>
                  <li>Communication preferences</li>
                  <li>Support ticket history</li>
                </ul>
              </div>
              <div className={styles.infoCard}>
                <div className={styles.cardIcon}>
                  <ComputerIcon />
                </div>
                <h3>Technical Information</h3>
                <ul>
                  <li>IP address and browser type</li>
                  <li>Device information</li>
                  <li>Pages visited and time spent</li>
                  <li>Referring website URLs</li>
                  <li>Cookies and similar technologies</li>
                </ul>
              </div>
            </div>
          </section>

          {/* How We Use Information */}
          <section id="usage" className={styles.section}>
            <div className={styles.sectionHeader}>
              <SecurityIcon />
              <h2>How We Use Your Information</h2>
            </div>
            <div className={styles.usageList}>
              <div className={styles.usageItem}>
                <CheckCircleIcon className={styles.checkmark} />
                <div>
                  <strong>Process Applications</strong>
                  <p>Review and process your accommodation applications, verify your student status, and manage your lease agreements.</p>
                </div>
              </div>
              <div className={styles.usageItem}>
                <CheckCircleIcon className={styles.checkmark} />
                <div>
                  <strong>Communicate With You</strong>
                  <p>Send important updates about your application, lease, maintenance requests, and community events.</p>
                </div>
              </div>
              <div className={styles.usageItem}>
                <CheckCircleIcon className={styles.checkmark} />
                <div>
                  <strong>Improve Our Services</strong>
                  <p>Analyze usage patterns to enhance user experience, optimize our website, and develop new features.</p>
                </div>
              </div>
              <div className={styles.usageItem}>
                <CheckCircleIcon className={styles.checkmark} />
                <div>
                  <strong>Legal Compliance</strong>
                  <p>Comply with applicable laws, regulations, and legal processes.</p>
                </div>
              </div>
              <div className={styles.usageItem}>
                <CheckCircleIcon className={styles.checkmark} />
                <div>
                  <strong>Security & Fraud Prevention</strong>
                  <p>Detect and prevent fraudulent activities, unauthorized access, and other security issues.</p>
                </div>
              </div>
            </div>
          </section>

          {/* Information Sharing */}
          <section id="sharing" className={styles.section}>
            <div className={styles.sectionHeader}>
              <GavelIcon />
              <h2>Information Sharing</h2>
            </div>
            <p>
              We do not sell, trade, or rent your personal information to third parties. We may share your 
              information in the following circumstances:
            </p>
            <ul className={styles.sharingList}>
              <li><strong>Service Providers:</strong> With trusted third parties who assist in operating our website, processing payments, or providing services (under confidentiality agreements).</li>
              <li><strong>Legal Requirements:</strong> When required by law, court order, or government regulation.</li>
              <li><strong>Emergency Situations:</strong> To protect the safety, property, or rights of Equity Residences, our residents, or others.</li>
              <li><strong>Business Transfers:</strong> In connection with a merger, acquisition, or sale of assets (with notice to affected users).</li>
              <li><strong>With Your Consent:</strong> When you explicitly authorize us to share your information.</li>
            </ul>
          </section>

          {/* Cookies & Tracking */}
          <section id="cookies" className={styles.section}>
            <div className={styles.sectionHeader}>
              <CookieIcon />
              <h2>Cookies & Tracking Technologies</h2>
            </div>
            <p>
              We use cookies and similar tracking technologies to enhance your browsing experience, 
              analyze website traffic, and personalize content. Cookies are small text files stored 
              on your device.
            </p>
            <div className={styles.cookieTypes}>
              <div className={styles.cookieType}>
                <h4>Essential Cookies</h4>
                <p>Required for basic website functionality and security.</p>
              </div>
              <div className={styles.cookieType}>
                <h4>Analytics Cookies</h4>
                <p>Help us understand how visitors interact with our website.</p>
              </div>
              <div className={styles.cookieType}>
                <h4>Preference Cookies</h4>
                <p>Remember your settings and preferences.</p>
              </div>
            </div>
            <p>
              You can control cookies through your browser settings. However, disabling cookies may 
              affect your ability to use certain features of our website.
            </p>
          </section>

          {/* Data Security */}
          <section id="security" className={styles.section}>
            <div className={styles.sectionHeader}>
              <SecurityIcon />
              <h2>Data Security</h2>
            </div>
            <p>
              We implement industry-standard security measures to protect your personal information 
              from unauthorized access, disclosure, alteration, or destruction. These include:
            </p>
            <div className={styles.securityGrid}>
              <div className={styles.securityItem}>
                <LockIcon />
                <span>SSL/TLS Encryption</span>
              </div>
              <div className={styles.securityItem}>
                <RouterIcon />
                <span>Firewall Protection</span>
              </div>
              <div className={styles.securityItem}>
                <StorageIcon />
                <span>Secure Data Centers</span>
              </div>
              <div className={styles.securityItem}>
                <PeopleIcon />
                <span>Access Controls</span>
              </div>
              <div className={styles.securityItem}>
                <AssessmentIcon />
                <span>Regular Security Audits</span>
              </div>
              <div className={styles.securityItem}>
                <VpnKeyIcon />
                <span>Multi-Factor Authentication</span>
              </div>
            </div>
            <div className={styles.note}>
              <strong>Note:</strong> While we strive to protect your information, no method of transmission 
              over the internet is 100% secure. We cannot guarantee absolute security.
            </div>
          </section>

          {/* Your Rights */}
          <section id="rights" className={styles.section}>
            <div className={styles.sectionHeader}>
              <PrivacyTipIcon />
              <h2>Your Privacy Rights</h2>
            </div>
            <p>Depending on your location, you may have the following rights regarding your personal information:</p>
            <ul className={styles.rightsList}>
              <li><strong>Access:</strong> Request a copy of the personal information we hold about you.</li>
              <li><strong>Correction:</strong> Request correction of inaccurate or incomplete information.</li>
              <li><strong>Deletion:</strong> Request deletion of your personal information (subject to legal obligations).</li>
              <li><strong>Restriction:</strong> Request restriction of processing your information.</li>
              <li><strong>Portability:</strong> Request transfer of your information to another service.</li>
              <li><strong>Objection:</strong> Object to certain processing activities.</li>
              <li><strong>Withdraw Consent:</strong> Withdraw your consent at any time.</li>
            </ul>
            <p>
              To exercise any of these rights, please contact us using the information below. We will 
              respond to your request within 30 days.
            </p>
          </section>

          {/* Children's Privacy */}
          <section id="children" className={styles.section}>
            <div className={styles.sectionHeader}>
              <PeopleIcon />
              <h2>Children's Privacy</h2>
            </div>
            <p>
              Our services are intended for students aged 18 and older. We do not knowingly collect 
              personal information from individuals under 18 years of age. If we become aware that 
              we have collected information from a child under 18, we will take steps to delete it 
              promptly. Parents or guardians who believe their child has provided us with personal 
              information should contact us immediately.
            </p>
          </section>

          {/* Updates to Policy */}
          <section id="updates" className={styles.section}>
            <div className={styles.sectionHeader}>
              <UpdateIcon />
              <h2>Updates to This Policy</h2>
            </div>
            <p>
              We may update this Privacy Policy from time to time to reflect changes in our practices 
              or legal requirements. We will notify you of any material changes by posting the new 
              policy on this page and updating the "Last Updated" date. We encourage you to review 
              this policy periodically.
            </p>
          </section>

          {/* Contact Us */}
          <section id="contact" className={styles.section}>
            <div className={styles.sectionHeader}>
              <ContactSupportIcon />
              <h2>Contact Us</h2>
            </div>
            <p>If you have any questions, concerns, or requests regarding this Privacy Policy or our data practices, please contact us:</p>
            <div className={styles.contactInfo}>
              <div className={styles.contactMethod}>
                <EmailIcon />
                <div>
                  <strong>Email:</strong>
                  <a href="mailto:info@equityhousing.co.za">info@equityhousing.co.za</a>
                </div>
              </div>
              <div className={styles.contactMethod}>
                <PhoneIcon />
                <div>
                  <strong>Phone:</strong>
                  <a href="tel:+27825808046">+27 82 580 8046</a>
                </div>
              </div>
              <div className={styles.contactMethod}>
                <LocationOnIcon />
                <div>
                  <strong>Address:</strong>
                  <span>94 Zastron Street, Bloemfontein , 9300</span>
                </div>
              </div>
            </div>
          </section>
        </div>
      </div>
    </div>
  );
};

export default Privacy;